import type { Country } from "../Country.js";
import { Currency } from "../Currency.js";
import type { EconomicSubjectVatType } from "../EconomicSubjectVatType.js";
import type { ExchangeRate } from "../ExchangeRatesSetting.js";
import type { ProductForCustomer } from "../Product.js";
import type { SubcategoryForCustomer } from "../Subcategory.js";
import type { SubscriptionParams } from "../Subscription.js";
import { SubscriptionSelection } from "../SubscriptionSelection.js";
import type { TaxedPrice } from "../TaxedPrice.js";
import { getFathersChoicePrice } from "../getFathersChoicePrice.js";
import { getMothersBlendPrice } from "../getMothersBlendPrice.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { convertToCurrency } from "./convertToCurrency.js";
import { findSubcategoryOfSubscriptionVariant } from "./findSubcategoryOfSubscriptionVariant.js";
import { shouldApplyTax } from "./shouldApplyTax.js";
import { subscriptionFrequencyToWeeksMap } from "./subscriptionFrequencyToWeeksMap.js";
import { subscriptionSizeToAmountMap } from "./subscriptionSizeToAmountMap.js";
import { subscriptionSizeToWeightMap } from "./subscriptionSizeToWeightMap.js";

export function calculatePriceOfSubscription(
	subcategories: SubcategoryForCustomer[],
	destinationCountry: Country | "outsideEu",
	subscription: SubscriptionParams,
	mothersBlendProduct: ProductForCustomer,
	cafeinaProduct: ProductForCustomer,
	currency: Currency,
	vatType: EconomicSubjectVatType,
	exchangeRates: ExchangeRate[],
	mothersBlendConstantPriceEnabled: boolean,
): TaxedPrice {
	const subcategory = findSubcategoryOfSubscriptionVariant(subcategories, subscription.variant, true);

	const taxRate = shouldApplyTax(destinationCountry, vatType)
		? subcategory.vatRates.find(({ country }) => country === destinationCountry)?.rate
		: 0;

	if (taxRate === undefined) {
		throw new Error(`Tax rate not found for country ${destinationCountry}`);
	}

	const amount = subscriptionSizeToAmountMap[subscription.size];
	let priceWithoutTax: number;

	if (subscription.selection === SubscriptionSelection.FathersChoice) {
		const priceWithoutTaxCzk = getFathersChoicePrice(subscription.size, subscription.frequency);
		priceWithoutTax = convertToCurrency(priceWithoutTaxCzk, Currency.CZK, currency, exchangeRates);
	} else if (subscription.selection === SubscriptionSelection.MothersBlend && mothersBlendConstantPriceEnabled) {
		// FCR-466 Temporary: Mother’s Blend subscription price comes from a code constant, not the DB price.
		const priceWithoutTaxCzk = getMothersBlendPrice(subscription.size, subscription.frequency);
		priceWithoutTax = convertToCurrency(priceWithoutTaxCzk, Currency.CZK, currency, exchangeRates);
	} else {
		const weeks = subscriptionFrequencyToWeeksMap[subscription.frequency];
		const weight = subscriptionSizeToWeightMap[subscription.size];
		const product = {
			[SubscriptionSelection.MothersBlend]: mothersBlendProduct,
			[SubscriptionSelection.Cafeina]: cafeinaProduct,
		}[subscription.selection];
		const variant = product.variants.find(({ weightInGrams }) => weightInGrams === weight);
		const taxedPrice = variant?.prices[currency][taxRate]?.price;
		if (!taxedPrice) {
			throw new Error("Price not found.");
		}
		const productPrice = taxedPrice.withoutTax * amount;
		priceWithoutTax = ((365 / (weeks * 7)) * productPrice) / 12;
	}

	return {
		taxRate,
		withTax: calculatePriceWithTax(priceWithoutTax, taxRate),
		withoutTax: priceWithoutTax,
	};
}
